import { computed, ref, unref } from 'vue'
import { getTranslation } from '@/api/aiRelativeTools'
import { TranslationLanguageCode } from '@/enums/translation'

export const AItranslationLanguage = Object.freeze({
  [TranslationLanguageCode.TW]: 'Traditional Chinese',
  [TranslationLanguageCode.CN]: 'Simplified Chinese',
  [TranslationLanguageCode.JP]: 'Japanese',
  [TranslationLanguageCode.TH]: 'Thai',
  [TranslationLanguageCode.EN]: 'English'
})

/**
 *
 * @param {*} props must wrapped with computed
 * @param {string} props.content
 * @param {string} props.template
 */
export function useTranslationGenerator (props) {
  const localValueMap = ref({})
  const loading = ref(false)
  const isError = ref(false)

  const originalProps = computed(() => unref(props))
  const isInvalid = computed(() => {
    return !originalProps.value || !originalProps.value.content
  })

  async function fetchTranslation (excludeLanguage = []) {
    if (isInvalid.value) {
      return
    }

    loading.value = true
    isError.value = false
    localValueMap.value = {}
    const aiTranslationLanguage = Object.keys(AItranslationLanguage)
      .filter(translation => !excludeLanguage.includes(translation))
      .map(lang => {
        return {
          code: lang,
          name: AItranslationLanguage[lang]
        }
      })

    // implement promise.allSettled
    const result = await Promise.all(
      aiTranslationLanguage.map(langInfo =>
        getTranslation({
          content: originalProps.value.content,
          lang: langInfo.name,
          ...(originalProps.value.template ? { template: originalProps.value.template } : {})
        })
          .then(val => ({ status: 'fulfilled', value: { ...val, lang_code: langInfo.code } }))
          .catch(err => ({ status: 'rejected', reason: err }))
      )
    )

    const hasError = result.some(result => result.status === 'rejected')

    if (hasError) {
      console.error('Translation error: >>', result)
      isError.value = true
    }

    localValueMap.value = result
      .filter(result => result.status === 'fulfilled')
      .reduce((acc, result) => {
        const lang = result.value.lang_code
        if (!acc[lang]) {
          acc[lang] = result.value.data.content
        }
        return acc
      }, {})

    loading.value = false
  }

  function resetTranslationGenerator () {
    loading.value = false
    localValueMap.value = {}
  }

  function getTranslationByLangCode (langCode) {
    if (!Object.values(localValueMap.value).length) {
      return ''
    }

    let result = ''
    switch (langCode) {
      case 'zh-CN':
        result = localValueMap.value['zh-CN']
        break
      case 'zh-TW':
      case 'zh-HK':
        result = localValueMap.value['zh-TW']
        break

      case 'ja-JP':
        result = localValueMap.value['ja-JP']
        break

      case 'th-TH':
        result = localValueMap.value['th-TH']
        break
      default:
        result = localValueMap.value['en-US']
        break
    }

    return result || ''
  }

  return {
    isInvalid,
    isError,
    value: localValueMap,
    loading,
    getTranslationByLangCode,
    fetchTranslation,
    resetTranslationGenerator
  }
}
